import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Injects a <script type="application/ld+json"> with the given JSON-LD payload
 * for schema.org structured data. Pass a single object or an array of objects.
 *
 * Usage:
 *   <JsonLd data={{ "@context": "https://schema.org", "@type": "Product", ... }} />
 *   <JsonLd data={[organizationSchema, websiteSchema]} />
 */
const JsonLd = ({ data }) => {
  if (!data) return null;
  // react-helmet-async accepts <script> tags inside Helmet
  const payload = JSON.stringify(data, null, 0);
  return (
    <Helmet>
      <script type="application/ld+json">{payload}</script>
    </Helmet>
  );
};

// ----------------- Schema builders -----------------

const origin = () => (typeof window !== "undefined" ? window.location.origin : "");

export const buildOrganizationSchema = (branding = {}) => {
  const url = origin();
  const name = branding.brand_name
    ? branding.brand_name.charAt(0).toUpperCase() + branding.brand_name.slice(1)
    : "Rieave";
  const logo = branding.logo_url ? (branding.logo_url.startsWith("http") ? branding.logo_url : url + branding.logo_url) : "";
  const sameAs = [];
  if (branding.instagram_url) sameAs.push(branding.instagram_url);
  if (branding.facebook_url) sameAs.push(branding.facebook_url);
  if (branding.twitter_url) sameAs.push(branding.twitter_url);
  const contact = [];
  if (branding.support_phone || branding.support_email) {
    contact.push({
      "@type": "ContactPoint",
      telephone: branding.support_phone || undefined,
      email: branding.support_email || undefined,
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: ["en"],
    });
  }
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name,
    url: url || undefined,
    logo: logo || undefined,
    sameAs: sameAs.length ? sameAs : undefined,
    contactPoint: contact.length ? contact : undefined,
  };
};

export const buildWebSiteSchema = (branding = {}) => {
  const url = origin();
  const name = branding.brand_name
    ? branding.brand_name.charAt(0).toUpperCase() + branding.brand_name.slice(1)
    : "Rieave";
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    url: url || undefined,
    potentialAction: {
      "@type": "SearchAction",
      target: `${url}/shop?search={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
};

export const buildBreadcrumbSchema = (items = []) => {
  // items: [{name, url}]
  if (!items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
};

export const buildProductSchema = (product, opts = {}) => {
  if (!product) return null;
  const url = origin() + `/product/${product.slug}`;
  const images = (product.images || []).map((img) =>
    img.startsWith("http") ? img : origin() + img
  );
  const price = Number(product.price || 0);
  const inStock = (product.stock || 0) > 0;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.short_description || product.seo_description || product.description || "",
    image: images,
    sku: product.sku || product.id,
    mpn: product.sku || undefined,
    brand: {
      "@type": "Brand",
      name: opts.brandName || "Rieave",
    },
    category: product.category_slug || undefined,
    url,
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: product.currency || "INR",
      price: price.toFixed(2),
      availability: inStock
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };
  // Aggregate rating (optional)
  if (product.rating && product.review_count) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(product.rating).toFixed(1),
      reviewCount: Number(product.review_count),
      bestRating: "5",
      worstRating: "1",
    };
  }
  return schema;
};

export const buildItemListSchema = (products = [], baseUrl = "") => {
  if (!products.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: (baseUrl || origin()) + `/product/${p.slug}`,
      name: p.name,
    })),
  };
};

export const buildCollectionPageSchema = ({ name, description, url }) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name,
  description: description || undefined,
  url,
});

export const buildWebPageSchema = ({ name, description, url, type = "WebPage" }) => ({
  "@context": "https://schema.org",
  "@type": type,
  name,
  description: description || undefined,
  url,
});

export const buildArticleSchema = (post, opts = {}) => {
  if (!post) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt || post.seo_description || "",
    image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: {
      "@type": "Person",
      name: post.author || (opts.brandName || "Rieave Editorial"),
    },
    publisher: {
      "@type": "Organization",
      name: opts.brandName || "Rieave",
      logo: opts.logo ? { "@type": "ImageObject", url: opts.logo } : undefined,
    },
    mainEntityOfPage: origin() + `/blog/${post.slug}`,
  };
};

export const buildFaqSchema = (faqs = []) => {
  const valid = (faqs || []).filter((f) => f.q && f.a);
  if (!valid.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: valid.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
};

export default JsonLd;
